import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { Plus, Minus, RotateCcw } from "lucide-react";
import { useLang } from "@/lib/i18n";

const COUNTIES_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3.0.1/counties-10m.json";

const COVERED = {
  12099: "palm-beach-county",
  12011: "broward-county",
  12086: "miami-dade-county",
  12085: "martin-county",
};

const CITIES = [
  { county: "martin-county", name: "Stuart", coords: [-80.2528, 27.1973] },
  { county: "martin-county", name: "Palm City", coords: [-80.2664, 27.1689] },
  { county: "martin-county", name: "Hobe Sound", coords: [-80.1373, 27.0658] },
  { county: "palm-beach-county", name: "Jupiter", coords: [-80.0942, 26.9342] },
  { county: "palm-beach-county", name: "PB Gardens", coords: [-80.1387, 26.8234] },
  { county: "palm-beach-county", name: "West Palm Beach", coords: [-80.0534, 26.7153] },
  { county: "palm-beach-county", name: "Wellington", coords: [-80.2706, 26.6587] },
  { county: "palm-beach-county", name: "Lake Worth Beach · HQ", coords: [-80.0728, 26.6168], hq: true },
  { county: "palm-beach-county", name: "Boynton Beach", coords: [-80.0664, 26.5253] },
  { county: "palm-beach-county", name: "Delray Beach", coords: [-80.0728, 26.4615] },
  { county: "palm-beach-county", name: "Boca Raton", coords: [-80.1289, 26.3683] },
  { county: "broward-county", name: "Deerfield Beach", coords: [-80.0998, 26.3184] },
  { county: "broward-county", name: "Coral Springs", coords: [-80.2706, 26.2710] },
  { county: "broward-county", name: "Pompano Beach", coords: [-80.1248, 26.2379] },
  { county: "broward-county", name: "Fort Lauderdale", coords: [-80.1373, 26.1224] },
  { county: "broward-county", name: "Plantation", coords: [-80.2331, 26.1276] },
  { county: "broward-county", name: "Hollywood", coords: [-80.1495, 26.0112] },
  { county: "miami-dade-county", name: "Aventura", coords: [-80.1392, 25.9565] },
  { county: "miami-dade-county", name: "Hialeah", coords: [-80.2781, 25.8576] },
  { county: "miami-dade-county", name: "Doral", coords: [-80.3553, 25.8195] },
  { county: "miami-dade-county", name: "Miami Beach", coords: [-80.1300, 25.7907] },
  { county: "miami-dade-county", name: "Miami", coords: [-80.1918, 25.7617] },
  { county: "miami-dade-county", name: "Coral Gables", coords: [-80.2685, 25.7215] },
  { county: "miami-dade-county", name: "Kendall", coords: [-80.3173, 25.6793] },
];

const INITIAL_CENTER = [-80.4, 26.3];
const INITIAL_ZOOM = 1;

export default function FloridaMap({ highlightSlug, compact = false }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [hoverCity, setHoverCity] = useState(null);
  const [position, setPosition] = useState({ coordinates: INITIAL_CENTER, zoom: INITIAL_ZOOM });

  const projectionConfig = compact
    ? { center: [-81, 26.4], scale: 5200 }
    : { center: [-81.4, 27.4], scale: 5600 };

  const handleZoomIn = useCallback(() => {
    setPosition((p) => ({ ...p, zoom: Math.min(p.zoom * 1.4, 6) }));
  }, []);
  const handleZoomOut = useCallback(() => {
    setPosition((p) => ({ ...p, zoom: Math.max(p.zoom / 1.4, 0.6) }));
  }, []);
  const handleReset = useCallback(() => {
    setPosition({ coordinates: INITIAL_CENTER, zoom: INITIAL_ZOOM });
  }, []);
  const handleMoveEnd = useCallback((pos) => setPosition(pos), []);

  return (
    <div
      data-testid="florida-map"
      className="relative w-full h-full min-h-[420px] rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm flex flex-col"
    >
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-1.5">
        <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-sm border border-slate-200">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 ring-2 ring-red-200" />
          <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
            {t.common.coverageZone}
          </p>
        </div>
        <p className="text-[10px] text-slate-500 pl-2">{t.common.hoverHint}</p>
      </div>

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-1.5 bg-white/95 backdrop-blur rounded-xl border border-slate-200 shadow-sm p-1">
        <button
          data-testid="map-zoom-in"
          onClick={handleZoomIn}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-700 hover:bg-slate-100"
          aria-label="Zoom in"
        >
          <Plus size={16} />
        </button>
        <button
          data-testid="map-zoom-out"
          onClick={handleZoomOut}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-700 hover:bg-slate-100"
          aria-label="Zoom out"
        >
          <Minus size={16} />
        </button>
        <button
          data-testid="map-zoom-reset"
          onClick={handleReset}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-700 hover:bg-slate-100"
          aria-label="Reset zoom"
        >
          <RotateCcw size={14} />
        </button>
      </div>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={projectionConfig}
        width={800}
        height={compact ? 700 : 880}
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%", display: "block", flex: 1 }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={0.6}
          maxZoom={6}
        >
          <Geographies geography={COUNTIES_URL}>
            {({ geographies }) =>
              geographies
                .filter((geo) => String(geo.id).startsWith("12"))
                .map((geo) => {
                  const fips = Number(geo.id);
                  const slug = COVERED[fips];
                  const isCovered = !!slug;
                  const isHighlight = highlightSlug ? slug === highlightSlug : isCovered;

                  const fill = isHighlight ? "#DC2626" : isCovered ? "#1E3A8A" : "#F1F5F9";
                  const stroke = isHighlight ? "#7F1D1D" : isCovered ? "#0F172A" : "#94A3B8";

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      data-testid={slug ? `map-county-${slug}` : undefined}
                      onClick={() => { if (slug) navigate(`/service-areas/${slug}`); }}
                      style={{
                        default: { fill, stroke, strokeWidth: isHighlight ? 1.4 : isCovered ? 1 : 0.5, outline: "none", cursor: slug ? "pointer" : "default", transition: "fill 0.2s" },
                        hover: { fill: isHighlight ? "#B91C1C" : isCovered ? "#1E40AF" : "#E2E8F0", outline: "none" },
                        pressed: { fill: isHighlight ? "#991B1B" : fill, outline: "none" },
                      }}
                    />
                  );
                })
            }
          </Geographies>

          {CITIES.map((c) => {
            const id = `${c.county}::${c.name}`;
            const isHover = hoverCity === id;
            const r = c.hq ? 6 : 3.5;
            const highlighted = !highlightSlug || c.county === highlightSlug;
            return (
              <Marker
                key={id}
                coordinates={c.coords}
                onMouseEnter={() => setHoverCity(id)}
                onMouseLeave={() => setHoverCity(null)}
                onClick={() => navigate(`/service-areas/${c.county}`)}
                style={{ cursor: "pointer" }}
              >
                <circle r={14 / position.zoom} fill="transparent" />
                <circle
                  r={r / Math.sqrt(position.zoom)}
                  fill={c.hq ? "#0F172A" : highlighted ? "#DC2626" : "#CBD5E1"}
                  stroke="white"
                  strokeWidth={(c.hq ? 2 : 1.4) / Math.sqrt(position.zoom)}
                />
                {c.hq && (
                  <circle r={11 / Math.sqrt(position.zoom)} fill="none" stroke="#FACC15" strokeWidth={1.5 / Math.sqrt(position.zoom)} />
                )}
                {isHover && (
                  <g pointerEvents="none" transform={`scale(${1 / Math.sqrt(position.zoom)})`}>
                    <rect
                      x={10}
                      y={-20}
                      width={c.name.length * 6.4 + 16}
                      height="22"
                      rx="4"
                      fill="#0F172A"
                    />
                    <text
                      x={18}
                      y={-5}
                      fontSize="11"
                      fontWeight="700"
                      fill="white"
                      fontFamily="Plus Jakarta Sans, sans-serif"
                    >
                      {c.name}
                    </text>
                  </g>
                )}
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-[11px] text-slate-500">
        <span className="bg-white/90 backdrop-blur px-2 py-1 rounded">
          {highlightSlug
            ? highlightSlug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
            : t.common.southFloridaCoverage}
        </span>
        <span className="bg-white/90 backdrop-blur px-2 py-1 rounded">{t.common.hq}</span>
      </div>
    </div>
  );
}
